import React, { useState } from 'react';
import {
  Box,
  IconButton,
  Typography,
  TextField,
  InputAdornment,
  Dialog,
  DialogActions,
  DialogContent
} from '@mui/material';
import { StaticDatePicker } from '@mui/x-date-pickers/StaticDatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import ptBr from "date-fns/locale/pt-BR";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { FiChevronLeft, FiChevronRight, FiSearch, FiCalendar } from 'react-icons/fi';
import { format } from 'date-fns';
import { DateNavContainer } from '../../styles';
import { FutButton } from '@/components/FutButton';

interface DateNavigationProps {
  date: Date;
  searchQuery: string;
  onDateChange: (direction: 'prev' | 'next') => void;
  onSearchChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onSpecificDateChange?: (date: Date) => void;
}

const DateNavigation: React.FC<DateNavigationProps> = ({
  date,
  searchQuery,
  onDateChange,
  onSearchChange,
  onSpecificDateChange
}) => {
  const [openDatePicker, setOpenDatePicker] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(date);

  const handleOpenDatePicker = () => {
    setOpenDatePicker(true);
  };

  const handleCloseDatePicker = () => {
    setOpenDatePicker(false);
  };
  
  // Função para aceitar a data selecionada
  const handleAccept = (newDate: Date | null) => {
    setSelectedDate(newDate);
    if (newDate && onSpecificDateChange) {
      onSpecificDateChange(newDate);
      handleCloseDatePicker();
    }
  };

  // Função para cancelar a seleção de data
  const handleCancel = () => {
    setSelectedDate(date);
    handleCloseDatePicker();
  };

  return (
    <Box 
      sx={{ 
        position: 'sticky', 
        top: 65, 
        zIndex: 1100,
        backgroundColor: 'background.paper',
        boxShadow: 1,
      }}
    >
      <DateNavContainer>
        <Box display="flex" alignItems="center">
          <IconButton onClick={() => onDateChange('prev')} aria-label="Dia anterior">
            <FiChevronLeft />
          </IconButton>
          
          <Box 
            onClick={handleOpenDatePicker} 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              cursor: 'pointer',
              mx: 1,
              padding: '4px 8px',
              borderRadius: 1,
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          >
            <Typography variant="body2" sx={{ mr: 0.5, lineHeight: "normal" }}>
              {format(date, 'dd/MM')}
            </Typography>
            <FiCalendar size={14} />
          </Box>
          
          <IconButton onClick={() => onDateChange('next')} aria-label="Próximo dia">
            <FiChevronRight />
          </IconButton>
        </Box>

        <TextField
          placeholder="Pesquisar partidas..."
          size="small"
          value={searchQuery}
          onChange={onSearchChange}
          sx={{ width: { xs: '100%', sm: '250px' }, ml: { xs: 0, sm: 2 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <FiSearch fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        
        <Dialog 
          open={openDatePicker} 
          onClose={handleCloseDatePicker}
          PaperProps={{
            sx: { 
              overflowY: 'visible',
              maxWidth: 'fit-content',
            }
          }}
        >
          <DialogContent sx={{ p: 0, overflowY: 'visible' }}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBr}>
              <StaticDatePicker
                displayStaticWrapperAs="desktop"
                value={selectedDate}
                onAccept={handleAccept}
              />
            </LocalizationProvider>
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'flex-end', px: 2, pb: 2, backgroundColor: 'background.paper', boxShadow: 1 }}>
            <FutButton onClick={handleCancel} variant="text">Cancelar</FutButton>
          </DialogActions>
        </Dialog>
      </DateNavContainer>
    </Box>
  );
};

export default DateNavigation;